import { type FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Typography, View } from '@shinetools/sunshine-universal';

import {
  type ChallengeMethod,
  ChallengeStateName,
  type VerifyChallengeOutput,
} from '__generated__/GQL';
import SunshineModal from 'components/_core/SunshineModal';

import { RejectStrongAuthenticationOperation } from '../utils/errors';
import ApprovedChallenge from './ApprovedChallenge';
import DeclinedChallenge from './DeclinedChallenge';
import { getMethodComponentInfo } from './getMethodComponentInfo';

export type WaitingForValidationChallengeProps = {
  isOpen: boolean;
  challengeId: string;
  onClose: (arg0: boolean) => void;
  isVerifying: boolean;
  isRequesting: boolean;
  method: ChallengeMethod;
  verifyMdaOperation: () => Promise<VerifyChallengeOutput | undefined>;
};

const WaitingForValidationChallenge: FC<WaitingForValidationChallengeProps> = ({
  isOpen,
  isRequesting,
  isVerifying,
  method,
  onClose,
  verifyMdaOperation,
}) => {
  const [error, setError] = useState<boolean>(false);
  const [isDeclined, setIsDeclined] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);

  useEffect(() => {
    const startVerificationWatcher = async () => {
      try {
        if (!isVerifying && !isRequesting) {
          const result = await verifyMdaOperation();
          if (result?.name === ChallengeStateName.ChallengeApproved) {
            setIsApproved(true);
          }
        }
      } catch (err) {
        if (
          err instanceof RejectStrongAuthenticationOperation &&
          [
            ChallengeStateName.ChallengeDeclinedNotInitiator,
            ChallengeStateName.ChallengeDeclined,
            ChallengeStateName.ChallengeExpired,
          ].includes(err.reason)
        ) {
          if (err.reason === ChallengeStateName.ChallengeExpired) {
            setIsExpired(true);
          } else {
            setIsDeclined(true);
          }
        } else {
          setError(true);
        }
      }
    };
    startVerificationWatcher();
  }, [isVerifying, isRequesting, verifyMdaOperation, setIsDeclined]);

  if (isOpen && isApproved) {
    return <ApprovedChallenge isOpen={isOpen} onClose={onClose} />;
  }

  if (isOpen && (isDeclined || isExpired)) {
    return (
      <DeclinedChallenge
        isDeclined={isDeclined}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  if (error) {
    return (
      <SunshineModal isOpen={isOpen} onClose={() => onClose(true)}>
        <SunshineModal.Footer>
          <Typography.Text marginTop="$space.16" variant="error">
            <FormattedMessage id="authentication.challenge.unknown_error" />
          </Typography.Text>
        </SunshineModal.Footer>
      </SunshineModal>
    );
  }

  const { description, image, title } = getMethodComponentInfo(method);

  return (
    <SunshineModal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={() => onClose(true)}
    >
      <SunshineModal.Header />

      <SunshineModal.Body padding={0}>
        <View backgroundColor="$grey.200" height={208} padding={0}>
          {image}
        </View>

        <View gap="$space.16" padding="$space.32">
          <SunshineModal.Title>{title}</SunshineModal.Title>
          <Typography.Text>{description}</Typography.Text>
        </View>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          hugContent={true}
          icon="cross"
          isLoading={isVerifying || isRequesting}
          onPress={() => {}}
          variant="secondary"
        >
          <FormattedMessage id="authentication.challenge.verifying" />
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default WaitingForValidationChallenge;
